import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  format_ist,
  care_api_error
} from '../../../util_components/care_request/care_request_rules';
import { load_session_change_history } from '../../../util_components/care_request/care_request_api';
import ResolveDialog from '../../../util_components/care_request/resolve_dialog';
import {
  match_view,
  resolve_parts,
  who_asked_view
} from '../../../util_components/care_request/who_asked_rules';
import {
  change_line,
  entry_meta,
  history_entries,
  screenshot_state,
  visible_entries
} from './change_history_rules';

// PROD-7828: who changed this session, when, what changed and why, with who asked (Care record) where recorded.
function ChangeHistoryPanel({ session_uuid, reload_token, on_resolved }) {
  const [state, setState] = useState({ loading: true, error: '', view: null });
  const [show_all, setShowAll] = useState(false);
  const [resolving, setResolving] = useState(null); // the request being resolved

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    load_session_change_history(session_uuid)
      .then((view) => setState({ loading: false, error: '', view }))
      .catch((e) =>
        setState({
          loading: false,
          error: care_api_error(e, 'Could not load the change history.').message,
          view: null
        })
      );
  }, [session_uuid]);

  useEffect(() => {
    if (session_uuid) load();
  }, [session_uuid, reload_token, load]);

  const entries = history_entries(state.view);
  const shown = visible_entries(entries, show_all);

  let body;
  if (state.loading && !state.view) {
    body = <p className="change-history-muted">Loading change history…</p>;
  } else if (state.error) {
    body = (
      <p className="change-history-error" role="alert">
        * {state.error}{' '}
        <button type="button" onClick={load}>
          Try again
        </button>
      </p>
    );
  } else if (entries.length === 0) {
    body = <p className="change-history-muted">No changes recorded yet.</p>;
  } else {
    body = (
      <>
        <ul className="change-history-list">
          {shown.map((e) => (
            <li key={e.id} className="change-history-item">
              <div className="change-history-meta">{entry_meta(e)}</div>
              <div className="change-history-title">{e.title}</div>
              {(e.changes || []).length > 0 ? (
                <ul className="change-history-changes">
                  {e.changes.map((c, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- labels can repeat; the order is fixed
                    <li key={`${c.label}-${i}`}>{change_line(c)}</li>
                  ))}
                </ul>
              ) : null}
              {e.note ? <div className="change-history-note">Note: {e.note}</div> : null}
              {e.request ? (
                <RequestDetails
                  request={e.request}
                  can_resolve={!!(state.view && state.view.can_resolve)}
                  onResolve={() => setResolving(e.request)}
                />
              ) : null}
            </li>
          ))}
        </ul>
        {entries.length > shown.length ? (
          <button type="button" onClick={() => setShowAll(true)}>
            Show all ({entries.length})
          </button>
        ) : null}
        {state.view && state.view.truncated ? (
          <p className="change-history-muted">Showing the latest {entries.length} changes only.</p>
        ) : null}
      </>
    );
  }

  return (
    <section className="change-history-panel" aria-label="Change history">
      <h3>Change history</h3>
      {body}
      <ResolveDialog
        record_uuid={resolving ? resolving.record_uuid : ''}
        who_asked={resolving ? resolving.who_asked : null}
        match={resolving ? resolving.match : null}
        parts={resolving ? resolve_parts(resolving.who_asked, resolving.match, true) : {}}
        onClose={() => setResolving(null)}
        onSaved={() => {
          setResolving(null);
          // the page bumps reload_token so the sibling panel (same records) reloads too
          if (on_resolved) on_resolved();
          else load();
        }}
      />
    </section>
  );
}

// Marker chip: "disagreement" (needs resolve) or "resolved"; the detail line is also the hover text.
function Marker({ kind, text, detail }) {
  if (!kind) return null;
  return (
    <span className={`who-asked-marker who-asked-marker-${kind}`} title={detail || undefined}>
      {text}
    </span>
  );
}

Marker.propTypes = { kind: PropTypes.string, text: PropTypes.string, detail: PropTypes.string };
Marker.defaultProps = { kind: '', text: '', detail: '' };

function RequestDetails({ request, can_resolve, onResolve }) {
  const shot = screenshot_state(request);
  const who = who_asked_view(request.who_asked, request.initiated_by);
  const match = match_view(request.match);
  const parts = resolve_parts(request.who_asked, request.match, can_resolve);
  return (
    <div className="change-history-request">
      <div>
        Request initiated by: <b>{who.shown}</b>{' '}
        <Marker kind={who.marker} text={who.marker_text} detail={who.detail} />
      </div>
      {who.detail ? <div className="change-history-muted">{who.detail}</div> : null}
      {match.marker ? (
        <div>
          <Marker kind={match.marker} text={match.marker_text} />
        </div>
      ) : null}
      {parts.answer || parts.match ? (
        <button type="button" onClick={onResolve}>
          Resolve
        </button>
      ) : null}
      <div>Requested at: {format_ist(request.requested_at)}</div>
      {request.note ? <div>Request note: {request.note}</div> : null}
      {shot === 'link' ? (
        <div>
          Screenshot:{' '}
          <a href={request.screenshot_url} target="_blank" rel="noopener noreferrer">
            View
          </a>
        </div>
      ) : null}
      {shot === 'unavailable' ? <div>Screenshot: link unavailable</div> : null}
      {request.recorded_by_name ? (
        <div className="change-history-muted">Recorded by {request.recorded_by_name}</div>
      ) : null}
    </div>
  );
}

RequestDetails.propTypes = {
  can_resolve: PropTypes.bool,
  onResolve: PropTypes.func,
  request: PropTypes.shape({
    record_uuid: PropTypes.string,
    who_asked: PropTypes.shape({ state: PropTypes.string }),
    match: PropTypes.shape({ state: PropTypes.string }),
    initiated_by: PropTypes.string,
    initiated_by_label: PropTypes.string,
    requested_at: PropTypes.string,
    note: PropTypes.string,
    has_screenshot: PropTypes.bool,
    screenshot_url: PropTypes.string,
    recorded_by_name: PropTypes.string
  }).isRequired
};

RequestDetails.defaultProps = { can_resolve: false, onResolve: () => {} };

ChangeHistoryPanel.propTypes = {
  session_uuid: PropTypes.string,
  reload_token: PropTypes.number,
  on_resolved: PropTypes.func
};

ChangeHistoryPanel.defaultProps = {
  session_uuid: '',
  reload_token: 0,
  on_resolved: null
};

export default ChangeHistoryPanel;
